import { create } from 'zustand';

import { shortcuts, type CommandSpec } from './shortcuts/index.js';

/**
 * Pie menus (Blender-style radial menus). A pie opens around the mouse on a
 * key press; flick toward an item and release the key to run it, or release
 * quickly and click an item. Items go round in Blender's order:
 * left, right, bottom, top, top-left, top-right, bottom-left, bottom-right.
 *
 *   registerPie({ id: 'scene.shading', label: 'Shading', items: () => [...] },
 *               { keys: ['Z'], context: 'scene' });
 */

export interface PieItem {
  id: string;
  label: string;
  icon?: string;
  /** Run a registered command (its key hint is shown). */
  commandId?: string;
  run?: () => void;
  /** Highlight as the current state (e.g. the active tab). */
  active?: boolean;
  disabled?: boolean;
}

export interface PieMenuDef {
  id: string;
  label: string;
  /** Built on open so items reflect current state. At most 8 are shown. */
  items: () => PieItem[];
}

const pies = new Map<string, PieMenuDef>();

/**
 * Register a pie and (unless `command` is false) a command `pie.<id>` that
 * opens it, bindable like any other shortcut.
 */
export function registerPie(def: PieMenuDef, command: Partial<CommandSpec> | false = {}): () => void {
  pies.set(def.id, def);
  const off =
    command === false
      ? () => undefined
      : shortcuts.register({
          id: `pie.${def.id}`,
          label: `Pie: ${def.label}`,
          category: 'Pie Menus',
          icon: 'layers',
          ...command,
          run: () => openPie(def.id),
        });
  return () => {
    if (pies.get(def.id) === def) pies.delete(def.id);
    off();
  };
}

export function getPie(id: string): PieMenuDef | undefined {
  return pies.get(id);
}

/** Last pointer position, so pies and menus open under the mouse. */
export const pointer = { x: 0, y: 0 };
if (typeof window !== 'undefined') {
  window.addEventListener(
    'pointermove',
    (e) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
    },
    { capture: true, passive: true },
  );
}

/**
 * Pie behaviour, as in Blender's Preferences › Interface › Pie Menus:
 * radius, the dead-zone threshold, how long a press counts as a tap (the pie
 * stays open) rather than a hold (release runs the pointed item), and an
 * optional confirm distance that runs an item as soon as the mouse travels it.
 */
export interface PieSettings {
  radius: number;
  threshold: number;
  tapMs: number;
  /** 0 = off. */
  confirm: number;
}

export const PIE_DEFAULTS: PieSettings = { radius: 104, threshold: 24, tapMs: 260, confirm: 0 };
const PIE_KEY = 'ai-studio:pie-settings';

function readPieSettings(): PieSettings {
  try {
    const raw = JSON.parse(localStorage.getItem(PIE_KEY) ?? '{}') as Partial<PieSettings>;
    const num = (v: unknown, d: number, lo: number, hi: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d);
    return {
      radius: num(raw.radius, PIE_DEFAULTS.radius, 60, 240),
      threshold: num(raw.threshold, PIE_DEFAULTS.threshold, 4, 80),
      tapMs: num(raw.tapMs, PIE_DEFAULTS.tapMs, 50, 1000),
      confirm: num(raw.confirm, PIE_DEFAULTS.confirm, 0, 400),
    };
  } catch {
    return { ...PIE_DEFAULTS };
  }
}

export const usePieSettings = create<PieSettings & { set: (patch: Partial<PieSettings>) => void; reset: () => void }>((set, get) => ({
  ...(typeof localStorage === 'undefined' ? PIE_DEFAULTS : readPieSettings()),
  set: (patch) => {
    set(patch);
    const { radius, threshold, tapMs, confirm } = get();
    try {
      localStorage.setItem(PIE_KEY, JSON.stringify({ radius, threshold, tapMs, confirm }));
    } catch {
      // not remembered
    }
  },
  reset: () => get().set({ ...PIE_DEFAULTS }),
}));

interface PieState {
  open: { id: string; x: number; y: number; at: number } | null;
}

export const usePieMenu = create<PieState>(() => ({ open: null }));

export function openPie(id: string, at: { x: number; y: number } = pointer): void {
  if (!pies.has(id)) return;
  const x = Math.min(Math.max(at.x || window.innerWidth / 2, 150), window.innerWidth - 150);
  const y = Math.min(Math.max(at.y || window.innerHeight / 2, 130), window.innerHeight - 130);
  usePieMenu.setState({ open: { id, x, y, at: performance.now() } });
}

export function closePie(): void {
  usePieMenu.setState({ open: null });
}

/** Slot angle in degrees (0 = right, counter-clockwise), Blender order. */
export const PIE_ANGLES = [180, 0, 270, 90, 135, 45, 225, 315] as const;

/** The slot a pointer offset points at, or -1 inside the dead zone. */
export function pieSlotAt(dx: number, dy: number, count: number, deadZone = 24): number {
  if (Math.hypot(dx, dy) < deadZone || count === 0) return -1;
  const angle = (Math.atan2(-dy, dx) * 180) / Math.PI;
  let best = -1;
  let bestDiff = Infinity;
  for (let i = 0; i < Math.min(count, PIE_ANGLES.length); i++) {
    let diff = Math.abs(angle - PIE_ANGLES[i]!) % 360;
    if (diff > 180) diff = 360 - diff;
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  }
  return best;
}
